<template>
  <cycle-radio-group
    v-model="model"
    class="opc-card-radio"
    :plans="currentPlans"
    :action="switchBillingCycle"
    :aria-label="`${planEnName} Billing Cycle`"
  >
    <zm-tooltip
      v-for="{ disabled, disabledTooltip, disabledInnerDesc, plan } in billingCycleList"
      :key="plan.billing_cycle"
      :disabled="!disabledTooltip"
    >
      <template #content>
        <div v-html="disabledTooltip" />
      </template>
      <zm-radio
        :data-testid="`checkout-radio-billing-cycle-${plan.plan_code}`"
        :label="plan.billing_cycle"
        :disabled="disabled"
        :class="{'opc-billing-cycle__radio': true }"
        :aria-describedby="`billing-cycle-${plan.billing_cycle}-description`"
        :aria-label="$t(`billing.billing_cycle.${plan.billing_cycle}`) + ' option'"
        :aria-required="false"
        :data-billing_cycle="plan.billing_cycle"
        :forward-tooltip-trigger="false"
      >
        <div :class="{'opc-zmone__radio-title': true }">
          {{ $t(`billing.billing_cycle.${plan.billing_cycle}`) }}
          <CycleCurrentBadge :value="plan" />
        </div>
        <subscription-cycle-sub-title :billingCycle="plan.billing_cycle" :currentCategory="plan.category" :show-link-mark="showAnnualTermMonthly" v-if="showAnnualTermMonthly"/>
        <p v-if="disabledInnerDesc" class="opc-billing-cycle__disabled__inner--desc">{{ disabledInnerDesc }}</p>
        <template v-else>
          <p class="opc-product-price-container" tabindex="-1" :aria-label="ariaPriceOfMonthForLabel(plan)">
            <span class="opc-product-price-container__price" aria-hidden="true">{{ getFormattedPriceOfMonthWithDownsell(plan, { forceDiff: true }) }}</span>
          </p>
          <p v-if="isTrial" class="opc-zmone__radio-price-unit opc-price-bill-text">
            <span v-html="$t('billing.trial.unit_suffix', [getMergedBilledText(plan.billing_cycle, plan.amount, unit)])"></span><priceVATTip />
          </p>
          <p v-else class="opc-zmone__radio-price-unit opc-price-bill-text">
            <span v-html="getMergedBilledText(plan.billing_cycle, getEffectiveAmount(plan), unit)"></span><priceVATTip />
          </p>
        </template>
      </zm-radio>
    </zm-tooltip>
  </cycle-radio-group>
</template>

<script>
import { get } from 'lodash';
import { BillingCycleEnum, BILLING_CYCLE } from '@/common/constants';
import { getPlanUnitWithCycle } from '@/common/product-unit';
import discountedMonthPrice from '@/common/mixin/discounted-month-price';
import { translateToEn } from '@/plugins/i18n';
import CycleCurrentBadge from '@/components/service/config-billing-cycle/cycle-current-badge.vue';
import { getEffectiveAmount } from '@/utils/downsell-price';
import SubscriptionCycleSubTitle from "@/plans/ZOOM_MEETING/components/subscription-cycle-sub-title.vue";

export default {
  name: 'BilledBillingCycleRadio',
  mixins: [discountedMonthPrice],
  components: {
    SubscriptionCycleSubTitle,
    CycleCurrentBadge,
  },
  emits: ['input', 'change'],
  props: {
    value: {
      type: String,
      default: '',
    },
    /**
     * [
     *  {
     *    plan: {
     *      amount: number,
     *      billing_cycle: string,
     *      cycleValue: number,
     *      ...,
     *    },
     *    disabled: boolean,
     *    disabledTooltip: string,
     *  }
     * ]
     */
    billingCycleList: {
      type: Array,
      default: () => [],
      required: true,
    },
    unit: {
      type: Number,
    },
    isTrial: {
      type: Boolean,
      default: false,
    },
    showAnnualTermMonthly: {
      type: Boolean,
      default: false,
    }
  },
  data() {
    return {
      BillingCycleEnum,
      BILLING_CYCLE,
      getPlanUnitWithCycle,
    };
  },
  computed: {
    model: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit('input', val);
      },
    },
    currentPlans() {
      return this.billingCycleList.map((l) => l.plan);
    },
    planEnName() {
      return translateToEn(get(this.currentPlans, [0, 'typeNameKey'], ''));
    }
  },
  methods: {
    switchBillingCycle(oldPlan, newPlan) {
      this.$emit('change', oldPlan, newPlan);
    },
    getEffectiveAmount,
  },
};
</script>

<style lang="scss" scoped>
.opc-billing-cycle {
  &__radio {
    flex-grow: 1;
    width: 100%;

    :deep {
      &.zbo-radio {
        line-height: 1;
        font-size: inherit;
        flex: 1 1 0;
        margin-bottom: 0;
        font-weight: 400;
        display: flex;
        align-items: flex-start;

        .zbo-radio__wrap {
          width: 100%;

          &.is-disabled {
            cursor: not-allowed;

            .zbo-radio__label {
              .opc-zmone__radio-title, .opc-zmone__radio-price, .opc-zmone__radio-price-unit, .opc-product-price-container, .opc-price-total-bill {
                color: var(--zoom-color-state-disable, #ADB1B8) !important;
              }
            }
          }
        }

        .zbo-radio__label {
          display: flex;
          flex-direction: column;
        }
      }
    }
  }
}

.opc-billing-cycle__disabled__inner--desc {
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  text-align: left;
  color: #515079;
  margin-top: 36px;
}

:deep(.opc-zmone__radio-title-billed-text) {
  margin-bottom: 4px;
}
</style>
